import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AlertsService, CLToastType } from '@clavisco/alerts';
import { Observable } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import { DataStorageService } from '../services/data-storage.service';
import { GlobalService } from '../services/global.service';
import { IPermission } from '../interfaces/i-permission';

/**
 * <summary>
 * Guard that checks if the user has the necessary permissions to access a route.
 * If the user doesn't have the required permission, they are redirected to the home page.
 * </summary>
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate {

  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private alertService: AlertsService,
    private dataStorageService: DataStorageService,
    private globalService: GlobalService
  ) {
  }

  /**
  * <summary>
  * Determines if the route can be activated by checking the user's permissions.
  * If the user doesn't have the required permission, they are redirected to the home page with an error message.
  * </summary>
  * @param route The route that is being navigated to.
  * @param state The state of the router at the time of navigation.
  * @returns A boolean, observable, or UrlTree indicating whether the route can be activated.
  */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    const actualPath: string = this.globalService.getRoutePath(route);
    const perms: IPermission[] = this.dataStorageService.GetItem<IPermission[]>('permissions');
    if (!perms) {
      this.HandledUnauthorizedAccess('/login');
    }
    const reqPerm: string = this.globalService.GetPermissionsByPath(actualPath);
    const hasPerm = this.globalService.ValidatePermission(perms, reqPerm);
    if (!hasPerm) {
      this.HandledUnauthorizedAccess('/home');
    }

    return true;
  }

  /**
   * <summary>
   * Handles unauthorized access by displaying an error toast and redirecting the user.
   * </summary>
   * @param redirectPath The path to which the user will be redirected.
   * @returns A boolean indicating that the route cannot be activated.
   */
  private HandledUnauthorizedAccess(redirectPath: string): boolean {
    this.alertService.Toast({ message: 'No tiene permisos para acceder a esta página', type: CLToastType.ERROR })
    const urlTree: UrlTree = this.router.createUrlTree([redirectPath], {});
    this.router.navigateByUrl(urlTree);
    return false;
  }

}
